import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MessageService, TreeNode } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription, combineLatest, lastValueFrom, take } from 'rxjs';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { ApiConstants } from 'src/app/base/api-constants';

import { Store } from '@ngrx/store';

import { uniq } from 'lodash-es';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { GlobalConstants } from 'src/app/base/global.constants';
import { FsBaseService } from 'src/app/services/fs-base.service';
import { ReferenceUtilService } from './reference-util.service';
import * as re from './reference.enum';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { EditorWithAutocompleteComponent } from '../editor-with-autocomplete/editor-with-autocomplete.component';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpBaseService } from 'src/app/base/http-base.service';

interface Column {
  field: string;
  header: string;
  fieldId: string;
}

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-reference-tree-view',
  templateUrl: './reference-tree-view.component.html',
  styleUrls: ['./reference-tree-view.component.scss'],
  standalone: false //NOSONAR
})
export class ReferenceTreeViewComponent implements OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  warningPropsCount = 0;
  private _nodesLoaded = false;
  private modalRef = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private appGlobalService = inject(AppGlobalService);
  private store = inject(Store);
  private referenceUtil = inject(ReferenceUtilService);
  private fsService = inject(FsBaseService);
  private loader = inject(AppLoaderService);
  columnName: string;
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private bs = inject(HttpBaseService);
  files: TreeNode[] = [];
  rawMissing: any[] = [];
  rawStale: any[] = [];
  activeTab: 'MISSING' | 'STALE' = 'MISSING';
  cols: Column[] = [];
  count = 10;
  pageExpanded = false;
  subscriptions: Subscription[] = [];
  ref$: Subscription = new Subscription();
  loading = false;
  appId = '';
  applicationMetaData: any = {};
  confirmationMessages: any = {};
  expandedColumn = 'componentType';
  showDataWarning = false;
  showCodeWarning = false;
  warnings: any = {
    dataWarning: '',
    codeWarning: ''
  };
  showErrorModal = false;
  modelAppIds: any[] = [];
  modelType: any = {
    dataModel: [
      'TABLE_GROUP',
      'TABLE',
      'FIELD',
      'FIELD_GROUP',
      'INDEX',
      'EXPORT',
      'IMPORT',
      'LOOKUP',
      'FPK',
      'MANDATORY_INPUT_FILTERS',
      'OPTIONAL_FILTER',
      'LOOKUP_TO',
      'LOOKUPTO_OUTPUT_MAPPING',
      'LOOKUPTO_FILTER_INPUT_MAPPING',
      'DISPLAY_FIELD',
      'FOREIGN_KEY',
      'TABLE_ALIAS',
      'FIELD_VALUE',
      'INDEX_DEFINITION',
      'LOOKUP_DEFINITION',
      'FPK_DEFINITION',
      'OPTIONAL_FILTER_FIELDS_DEFINITION',
      'LOOKUPTO_OUTPUT_MAPPING_DEFINITION',
      'LOOKUPTO_FILTER_INPUT_MAPPING_DEFINITION',
      'FOREIGN_KEY',
      'MANDATORY_INPUT_FILTERS_DEFINITION',
      'UNIQUE_IDENTIFIER_DEFINITION',
      'DATA_POPULATION_STRATEGY',
      'ACL_CUSTOM_ACTION',
      'MAIL_INFO',
      'LOOKUP_TEMPLATE',
      'UNIQUE_FIELDS_FOR_LOOKUP',
      'QUERYVIEW_CUD_CONFIGURATION',
      'RUNTIME_CONFIGURATION',
      'SEARCHABLE_INDEX_FIELD'
    ],
    pages: ['PAGES', 'PAGE'],
    workflow: [
      'WORKFLOW',
      'WORKFLOW_ACTOR',
      'WORKFLOW_ACTION',
      'WORKFLOW_ACTION_BUTTON',
      'WORKFLOW_HISTORY_BUTTON',
      'WORKFLOW_MAIL_INFO',
      'WORKFLOW_ORDERING_INFO'
    ],
    roles: ['ROLE'],
    customComponent: ['CUSTOM_COMPONENT'],
    themes: ['THEMES'],
    appSettings: ['TECHNOLOGY_CHOICES', 'APP_SETTINGS', 'APP', 'ORGANZIATION', 'ENVIRONMENT', 'APPLICATION_CONFIGURATION'],
    service: ['SERVICE'],
    template: ['TEMPLATE'],
    queryBuilder: ['QUERY_BUILDER'],
    userStories: ['USERSTORY'],
    apis: ['EXPOSED_API_INFO', 'CONSUMER', 'EXPOSURE', 'PROVIDER'],
    database: ['DATABASE'],
    queryString: ['QUERY_STRING']
  };

  hasChildReference: any = [
    'QUERY_BUILDER',
    'INDEX',
    'FOREIGN_KEY_PARENT_INDEX',
    'TABLE_PAGE',
    'WORKFLOW_ACTION_STEP',
    'ROLE_FIELD',
    'LOOKUP_FIELDS'
  ];

  excluded: any = ['TABLE_INDEX', 'TABLE_FOREIGN_KEY', 'TABLE_LOOKUP_DEFINITION', 'CUD_CONFIGURATION'];

  itemName = '';
  itemType = '';
  itemAction = '';
  itemid: string = '';

  editorOptions = {
    language: 'sql',
    theme: 'vs',
    automaticLayout: true,
    fontSize: 14
  };
  currentDatamodel: any = null;
  @ViewChild(EditorWithAutocompleteComponent) queryEditor: EditorWithAutocompleteComponent;

  updateLatestQueryString(event) {
    this.referenceUtil.requestRefresh(event);
  }

  get isBetaAccessPrivilegeConsumable(): boolean {
    return this.appGlobalService.betaAccessPrivilegeForApp;
  }

  public readonly warningPropertyNames: readonly string[] = Object.values(re.ReferenceWarningProperty);

  setDefaultInfo() {
    this.applicationMetaData = this.appGlobalService.get('appmetaInfo');
    const referenceList = this.config?.data?.referenceList;
    const refModelId =
      Array.isArray(referenceList) && referenceList.length > 0 ? referenceList[0]?.referredModelId : null;

    // Handle multiple field IDs in node name by resolving each to its name from metadata
    let nodeName = this.config?.data?.node?.name || '';
    if (nodeName && nodeName.includes(',')) {
      const fieldIds = nodeName.split(',').map((id: string) => id.trim());
      const fieldNames = fieldIds
        .map((id: string) => this.applicationMetaData[id] || id)
        .join(', ');
      nodeName = fieldNames;
    }

    this.itemName =
      this.config?.data?.nameOverride ||
      this.applicationMetaData[this.config?.data?.additionalProps?.name] ||
      this.applicationMetaData[refModelId] ||
      nodeName ||
      '';
    this.itemType =
      this.referenceUtil.snakeToTitleCase(this.config?.data?.additionalProps?.type || this.config?.data?.node?.type) ||
      '';
    this.itemAction = this.config?.data?.action || '';
    this.itemid = this.config?.data?.sid || null;
  }
  columnsetter(columnName: string) {
    const baseCols: Column[] = [
      { field: 'componentType', header: 'REFERENCE_HEADER_COLUMN_COMPONENT', fieldId: 'componentType' },
      { field: 'usedIn', header: 'REFERENCE_HEADER_COLUMN_USED_IN', fieldId: 'usedIn' }
    ];
    const hasValidateMode = this.config?.data?.validateMode || false;
    if (hasValidateMode && this.config?.data?.validateTabs) {
      this.rawMissing = Array.isArray(this.config?.data?.missingReferences) ? this.config?.data.missingReferences : [];
      this.rawStale = Array.isArray(this.config?.data?.staleReferences) ? this.config?.data.staleReferences : [];
    }
    this.cols =
      hasValidateMode || this.warningPropsCount < 1 || this.itemAction === 'WHERE_USED'
        ? baseCols
        : [
            ...baseCols,
            {
              field: 'onDeleteConfirmation',
              header: `On ${this.columnName} Confirmation`,
              fieldId: 'onDeleteConfirmation'
            }
          ];
    return hasValidateMode;
  }
  ngOnInit() {
    this.setDefaultInfo();
    this.columnName = 'Update';
    if (this.itemAction === 'DELETE') {
      this.columnName = 'Delete';
    } else if (this.itemAction === 'RENAME') {
      this.columnName = 'Rename';
    }
    const hasValidateMode = this.columnsetter(this.columnName);

    this.loading = true;
    this.appId = this.appGlobalService.get('appId');
    this.applicationMetaData = this.appGlobalService.get('refAppMetaInfos')
      ? { ...this.appGlobalService.get('refAppMetaInfos'), ...this.appGlobalService.get('appmetaInfo') }
      : this.appGlobalService.get('appmetaInfo');
    this.confirmationMessages = this.appGlobalService.get('referenceMessageBundle');
    this.showDataWarning = this.appGlobalService.get('dataAlreadyPresent');
    this.showCodeWarning = this.appGlobalService.get('customCodeAdded');

    const hasReferenceList = (val: unknown): val is { referenceList: unknown[] } =>
      !!val && typeof val === 'object' && 'referenceList' in val;
    if (hasValidateMode && hasReferenceList(this.config?.data) && this.config.data.referenceList) {
      this.loadNodes();
    }
  }

  onTabChange(tab: 'MISSING' | 'STALE'): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    if (this.config?.data?.validateTabs) {
      this.config.data.referenceList = tab === 'MISSING' ? this.rawMissing : this.rawStale;
      if (this.config.data.referenceList?.length > 0) {
        this.loading = true;
        this.loader.show();
      }
      this.loadNodes(true);
    }
  }

  getReferences(modelId: string, _deprecatedApiName?: string) {
    const nodeTypeRaw =
      this.config?.data?.node?.type || this.config?.data?.additionalProps?.type || this.itemType || 'GENERIC';
    const normalized = String(nodeTypeRaw).replace(/\s+/g, '').toUpperCase();
    const modelTypeMap: Record<string, string> = {
      INDEXES: 'INDEX_DEFINITION',
      INDEX: 'INDEX_DEFINITION',
      FIELDS: 'FIELD',
      FIELDGROUP: 'FIELD_GROUP',
      GROUP: 'TABLE_GROUP',
      PAGES: 'PAGE',
      PAGEVIEW: 'PAGE',
      WORKFLOWACTION: 'WORKFLOW_ACTION',
      WORKFLOWACTOR: 'WORKFLOW_ACTOR',
      CUSTOMCOMPONENT: 'CUSTOM_COMPONENT',
      QUERYSTRING: 'QUERY_STRING'
    };
    const inferredType = modelTypeMap[normalized] || normalized;

    const baseActionType = (this.itemAction || 'UPDATE').toUpperCase();
    const actionType = this.getActionType(inferredType, baseActionType);
    return this.referenceUtil.getReferences({
      modelId,
      modelType: inferredType,
      actionType,
      action: actionType
    }) as Promise<unknown>;
  }

  getActionType(modelType: string, baseAction: string): string {
    const actionTypeMap: Record<string, Record<string, string>> = {
      QUERY_STRING: {
        DELETE: 'QUERY_STRING_DELETE'
      }
    };

    const typeActions = actionTypeMap[modelType];
    if (typeActions && typeActions[baseAction]) {
      return typeActions[baseAction];
    }

    return baseAction;
  }

  nodeSelect(_e: unknown): void {
    /* intentional no-op */
  }

  getRefMetaDataFromFS(refAppId): Observable<any> {
    const version = this.appGlobalService.get('appVersion');
    const queryObject = {
      collectionName: `AppMetaInfo_${version}`,
      filter: [
        {
          field: 'appId',
          operator: '==',
          value: refAppId
        },
        { field: 'recDeleted', operator: '==', value: false }
      ]
    };
    return this.fsService.fireService(queryObject);
  }

  formTreeStructure(groupedData) {
    const warningProps = this.warningPropertyNames;
    const warningRefs = groupedData.filter((ref) => {
      if (
        (ref.modelType === 'QUERY_BUILDER' && ref.propertyName === 'DEFAULT_FILTER_PAGE_VARIABLE') ||
        (ref.modelType !== 'QUERY_BUILDER' &&
          ref.propertyName !== 'CHILD_PAGE_TABLE' &&
          ref.propertyName !== 'WORKFLOW_TIMELINE_BUTTON')
      ) {
        return warningProps.includes(ref.propertyName);
      } else if (
        ref.modelType === 'QUERY_BUILDER' &&
        Array.isArray(ref.childReferences) &&
        ref.childReferences.length > 0
      ) {
        return warningProps.includes(ref.propertyName);
      }
      return false;
    });

    const pushRefNode = (ref: any, setWarningFlag = false) => {
      if (setWarningFlag) this.setWarning(ref);
      const nestedChildren = this.buildNestedChildren(ref?.childReferences || [], ref?.referredModelType);
      const refNode = {
        data: ref,
        expand: this.checkModelhasChildReference(ref),
        children: nestedChildren,
        expanded: nestedChildren.length > 0,
        hasChildElements: nestedChildren.length > 0
      };
      this.files.push(refNode);
    };

    let sortedData: any[];
    if (!this?.config?.data?.validateMode) {
      sortedData = [...warningRefs, ...groupedData.filter((ref) => !warningProps.includes(ref.propertyName))];
    } else {
      sortedData = groupedData;
    }

    sortedData.forEach((ref: any, index: number) => {
      if (
        (ref.modelType === 'QUERY_BUILDER' && ref.propertyName === 'DEFAULT_FILTER_PAGE_VARIABLE') ||
        (ref.modelType === 'QUERY_BUILDER' && this?.config?.data?.validateMode) ||
        (ref.modelType !== 'QUERY_BUILDER' &&
          ref.propertyName !== 'CHILD_PAGE_TABLE' &&
          ref.propertyName !== 'WORKFLOW_TIMELINE_BUTTON')
      ) {
        this.enrichRef(ref);
        pushRefNode(ref, index === 0);
      } else if (
        ref.modelType === 'QUERY_BUILDER' &&
        Array.isArray(ref?.childReferences) &&
        ref.childReferences.length > 0
      ) {
        (ref.childReferences || []).forEach((childRef: any, childIndex: number) => {
          this.enrichRef(childRef);
          pushRefNode(childRef, index === 0 && childIndex === 0);
        });
      }
    });

    if (this.config?.data.parentTableDelete) {
      const fkFieldNode = this.files.find((f: TreeNode) => f.data && f.data.propertyName === 'FOREIGN_KEY_FIELDS');
      if (fkFieldNode && fkFieldNode.data) {
        fkFieldNode.data.manualChange = false;
        fkFieldNode.data.type = 'Auto';
      }
    }

    // Sort by componentType
    this.files.sort((a, b) => {
      const typeA = (a.data?.componentType || '').toLowerCase();
      const typeB = (b.data?.componentType || '').toLowerCase();
      return typeA.localeCompare(typeB);
    });

    this.files = [...this.files];
    this.warningPropsCount = this.countWarningsInTree(this.files);
    this.columnsetter(this.columnName);
    if (this.files.length == 0) {
      this.closePopup('close', null);
      this.showDeleteConfirmationOrClose();
    }
  }

  loadNodes(force: boolean = false) {
    if (this._nodesLoaded && !force) {
      return;
    }
    if (!force) {
      this._nodesLoaded = true;
    }
    setTimeout(() => {
      this._loadNodesInternal();
    });
  }

  private _loadNodesInternal() {
    const queryBuilderReferences: any = [];
    this.files = [];
    if (this.config.data.referenceList?.length > 0) {
      let groupedData = this.filterData(this.config.data.referenceList, this.config?.data?.node?.f_tableId || '');
      // Filter out excluded property names only for WHERE_USED action
      if (this.itemAction === 'WHERE_USED') {
        groupedData = groupedData.filter((d: any) => !this.excluded.includes(d.propertyName));
      }
      this.modelAppIds = uniq(groupedData.filter((d: any) => d.appId !== this.appId).map((dt: any) => dt.appId));
      if (this.modelAppIds.length) {
        this.loading = true;
        const metaObservables = this.modelAppIds.map((id) => this.getRefMetaDataFromFS(id).pipe(take(1)));
        const sub = combineLatest(metaObservables).subscribe({
          next: (valuearr: any[]) => {
            this.loading = false;
            this.loader.hide();
            if (valuearr?.length) {
              const d = JSON.parse(JSON.stringify(this.applicationMetaData)) || {};
              valuearr.forEach((metdata) => {
                if (metdata) {
                  metdata.forEach((dt: any) => {
                    const rec = dt.payload.doc.data();
                    d[rec.modelId] = rec.name;
                  });
                }
              });
              this.applicationMetaData = d;
              this.formTreeStructure(groupedData);
              this.expandall();
            }
          },
          error: () => {
            this.loading = false;
            this.loader.hide();
          }
        });
        this.addToSubscriptionsList(sub);
      } else {
        this.loading = false;
        this.loader.hide();
        this.formTreeStructure(groupedData);
        this.expandall();
      }
    } else if (this.itemType.toLowerCase() === this.translate.instant('QUERY_STRING').toLowerCase()) {
      if (this.files.length == 0) {
        this.showDeleteConfirmationOrClose();
      }
    }
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }
  public expandall(): void {
    const temp = this.files;
    temp.forEach((node: any) => {
      if (node?.expand?.length > 0) {
        this.onNodeExpand(node);
      }
    });
  }

  filterData(data, conditionId) {
    const filteredObjects = data.filter(
      (item) =>
        conditionId &&
        (item?.additionalInfo?.tableId === conditionId || item.additionalInfo?.path?.split('/')?.includes(conditionId))
    );
    // Remove filtered objects from the original array
    data = data.filter((item) => item?.additionalInfo?.tableId !== conditionId);
    // Push the filtered objects to the beginning of the array
    data.unshift(...filteredObjects);

    const groupedData = data.reduce((result, item) => {
      const group = result.find((grp) => grp[0]?.modelId === item.modelId);
      if (group) {
        group.push(item);
      } else {
        result.push([item]);
      }
      return result;
    }, []);
    const d = uniq(groupedData.flat());
    return d;
  }

  setWarning(ref: any) {
    ref.warning = true;
    const warningMessages = this.referenceUtil.getWarningMessages(
      ref,
      this.itemAction,
      this.applicationMetaData,
      this.config?.data
    );
    this.warnings.dataWarning = warningMessages.dataWarning;
    this.warnings.codeWarning = warningMessages.codeWarning;
  }

  groupModelByType(ref: any) {
    let componentType = '';
    Object.keys(this.modelType).map((component: any) => {
      if (this.modelType[component].includes(ref.modelType)) {
        componentType = re.ComponentType[component];
      }
    });
    return componentType;
  }

  maximizeModal() {
    if (!this.pageExpanded) {
      $('#reference-tree-view').css({ height: '100%', width: '100%', 'max-height': '100%' });
      $('.reference-tree').css({ height: '100%', width: '100%', 'max-height': '100%', 'border-radius': '0px' });
    } else {
      $('#reference-tree-view').css({ height: '', width: '', 'max-height': '' });
      $('.reference-tree').css({ height: '', width: '', 'max-height': '', 'border-radius': '8px' });
    }
    this.pageExpanded = !this.pageExpanded;
  }

  onNodeExpand(event: any, fromPage?: any) {
    this.loading = true;
    const node = fromPage ? event.node : event;
    const parentReferredModelType = node.data?.referredModelType;
    const childReferences = node.data?.childReferences || [];
    if (!childReferences.length) {
      this.loading = false;
      this.loader.hide();
      return;
    }
    this.formChildNodes(node, childReferences, parentReferredModelType);
  }

  onNodeCollapse(event: any) {
    event.node.hasChildElements = false;
    event.node.children = [];
  }

  //not used for now

  toggleListBasedonSelection(event: any, cn: string, expandedColumn: string) {
    let toggleClassName: any[] = [];
    let currentLevel = '';
    for (const className of event.originalEvent.target.parentElement.classList.values()) {
      if (className.startsWith('tree-toggle-level')) {
        toggleClassName = className.split('-');
        currentLevel = toggleClassName[toggleClassName.length - 1];
      }
    }
    this.expandedColumn = expandedColumn;
    setTimeout(() => {
      $(`#treeTableToggler-${cn}-${currentLevel} button i`).removeClass('pi-fw pi-chevron-down');
      $(`#treeTableToggler-${cn}-${currentLevel} button i`).addClass('pi-fw pi-chevron-right');
    }, 100);
  }

  formTreeforchildren(groupedData, parentReferredModelType, node, newValue) {
    this.loading = false;
    this.loader.hide();

    groupedData.map((ref: any, index: number) => {
      ref.parentReferredModelType = parentReferredModelType;
      this.enrichRef(ref);
      if (ref.propertyName !== 'CHILD_PAGE_TABLE' && ref.propertyName !== 'WORKFLOW_FIELDS_VALUES') {
        const nestedChildren = this.buildNestedChildren(ref?.childReferences || [], ref?.referredModelType);
        node.children.push({
          data: ref,
          expand: this.checkModelhasChildReference(ref),
          children: nestedChildren,
          expanded: nestedChildren.length > 0,
          hasChildElements: nestedChildren.length > 0,
          hasParentNode: ['componentType'],
          levelLastNode: index + 1 === newValue.length,
          firstNode: !!(index === 0 && index + 1 !== newValue.length)
        });
      }
    });
    this.files = [...this.files];
    if (this.files.length == 0) {
      this.showDeleteConfirmationOrClose();
    }
  }

  formChildNodes(node: any, newValue: any, parentReferredModelType: string) {
    node.expanded = true;
    node.hasChildElements = true;
    node.children = [];
    this.loading = true;
    const groupedData = this.filterData(newValue, this.config?.data?.node?.f_tableId || '');
    this.modelAppIds = uniq(groupedData.filter((d: any) => d.appId !== this.appId).map((dt: any) => dt.appId));
    if (this.modelAppIds.length) {
      // logic to get metainfo for different apps (first snapshot only per app)
      const metaObservables = this.modelAppIds.map((id) => this.getRefMetaDataFromFS(id).pipe(take(1)));
      const sub = combineLatest(metaObservables).subscribe({
        next: (valuearr: any[]) => {
          this.loading = false;
          this.loader.hide();
          if (valuearr?.length) {
            const d = JSON.parse(JSON.stringify(this.applicationMetaData)) || {};
            valuearr.forEach((metdata) => {
              if (metdata) {
                metdata.forEach((dt: any) => {
                  const rec = dt.payload.doc.data();
                  d[rec.modelId] = rec.name;
                });
              }
            });
            this.applicationMetaData = d;
          }
          this.formTreeforchildren(groupedData, parentReferredModelType, node, newValue);
        },
        error: () => {
          this.loading = false; // Reset loading on error
          this.loader.hide();
        }
      });
      this.addToSubscriptionsList(sub);
    } else {
      this.formTreeforchildren(groupedData, parentReferredModelType, node, newValue);
    }
  }

  checkModelhasChildReference(reference: any) {
    return reference?.childReferences?.length > 0 ? ['componentType'] : [];
  }

  enrichRef(ref: any): void {
    ref.type = GlobalConstants.auto;
    ref.componentType = this.groupModelByType(ref);
    ref.usedIn = this.referenceUtil.getUsedInColumn({ data: ref }, this.applicationMetaData);
    ref.onDeleteConfirmation = this.referenceUtil.getMessageBasedonInfo(
      this.itemAction,
      ref,
      this.applicationMetaData,
      this.config?.data
    );
  }

  /**
   * Recursively count warnings in tree nodes using the same condition as the template
   */
  private countWarningsInTree(nodes: TreeNode[]): number {
    let count = 0;
    for (const node of nodes) {
      const data = node.data;
      if (
        this.warningPropertyNames.includes(data?.propertyName) ||
        data?.referredModelType === 'QUERY_BUILDER'
      ) {
        count++;
      }
      if (node.children && node.children.length > 0) {
        count += this.countWarningsInTree(node.children);
      }
    }
    return count;
  }

  buildNestedChildren(childRefs: any[], parentReferredModelType?: string): any[] {
    if (!Array.isArray(childRefs) || childRefs.length === 0) {
      return [];
    }
    return childRefs
      .filter(
        (childRef) =>
          childRef?.propertyName !== 'CHILD_PAGE_TABLE' && childRef?.propertyName !== 'WORKFLOW_FIELDS_VALUES'
      )
      .map((childRef: any, index: number) => {
        this.enrichRef(childRef);
        if (parentReferredModelType) {
          childRef.parentReferredModelType = parentReferredModelType;
        }
        const nestedChildren = this.buildNestedChildren(childRef?.childReferences || [], childRef?.referredModelType);
        return {
          data: childRef,
          expand: this.checkModelhasChildReference(childRef),
          children: nestedChildren,
          expanded: nestedChildren.length > 0,
          hasChildElements: nestedChildren.length > 0,
          hasParentNode: ['componentType'],
          levelLastNode: index + 1 === childRefs.length,
          firstNode: !!(index === 0 && index + 1 !== childRefs.length)
        };
      });
  }

  closePopup(action = 'close', data?: any) {
    if (action === 'close') {
      this.modalRef.close({ confirm: false });
    } else {
      data.confirm = true;
      this.modalRef.close(data);
    }
    this.modalRef.destroy();
  }

  /**
   * Show confirmation dialog when there are zero references and action is DELETE
   * Otherwise, just close the popup
   */
  private showDeleteConfirmationOrClose() {
    if (this.itemAction === 'DELETE') {
      this.confirmationDialogService.confirm({
        message: this.translate.instant('DELETE_ITEM_CONFIRMATION', { name: this.itemName }),
        header: this.translate.instant('CONFIRM'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          // User confirmed deletion - close with confirm: true
          this.modalRef.close({ confirm: true });
          this.modalRef.destroy();
        },
        reject: () => {
          // User cancelled - close without confirming
          this.modalRef.close({ confirm: false });
          this.modalRef.destroy();
        }
      });
    } else {
      this.closePopup('close', null);
    }
  }

  generateArray(node: any) {
    const number = node?.node?.hasChildElements ? node.level + 1 : node.level;
    return Array.from(Array(number).keys());
  }

  drawLines(rowNode: any, index: number, col: any, length) {
    const i = index + 1;
    return (
      (rowNode.node?.hasParentNode?.includes(col.field) && rowNode.parent && this.getDeep(rowNode, i)) ||
      (index + 1 >= length && rowNode.node.hasChildElements)
    );
  }

  getDeep(rowNode: any, index: number) {
    // hide the vertical line for last node of the tree..
    let p = '';
    for (let i = index; i < rowNode.level; i++) {
      if (p) {
        p = `${p}.parent`;
      } else {
        p = `parent`;
      }
    }
    let parent = this.objectbyString(rowNode, p);
    if (index === rowNode.level && !parent) {
      parent = rowNode.parent;
    }

    return parent && (!parent?.levelLastNode || (parent?.levelLastNode && index === rowNode.level));
  }

  objectbyString = function (o, s) {
    s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    s = s.replace(/^\./, ''); // strip a leading dot
    const a = s.split('.');
    for (let i = 0, n = a.length; i < n; ++i) {
      const k = a[i];
      if (k in o) {
        o = o[k];
      } else {
        return;
      }
    }
    return o;
  };

  addToSubscriptionsList(subName) {
    this.subscriptions.push(subName);
  }

  editQuery(node) {
    const modelType = node.data?.modelType || node.data?.referredModelType;
    const modelId = node.data?.modelId;

    if (!modelId) {
      return;
    }

    // Check if the reference is a QueryString
    if (modelType === 'QUERY_STRING') {
      this.fetchAndEditQueryString(modelId);
    } else {
      // Default to DataModel
      this.fetchAndEditDataModel(modelId);
    }
  }

  /**
   * Fetch QueryString by SID and open editor
   */
  private fetchAndEditQueryString(sid: string) {
    this.bs.get(ApiConstants.getQueryStringBySid, { sid }).subscribe({
      next: (queryString: any) => {
        if (queryString && queryString.value) {
          this.currentDatamodel = queryString;
          this.currentDatamodel.isQueryString = true; // Flag to identify type during save

          // Replace SIDs with names for display
          const queryContent = queryString.value;
          this.queryEditor.value = this.queryEditor.replacePlaceholders(queryContent, this.applicationMetaData);
          this.queryEditor.currenttablename = queryString.name;
          this.queryEditor.isQueryStringEditor = true; // Flag to show 'Query String' in header
          this.queryEditor.toggleEditor();
        }
      },
      error: (error) => {
        // Handle error if needed
      }
    });
  }

  /**
   * Fetch DataModel table data and open editor
   */
  private fetchAndEditDataModel(sid: string) {
    this.bs.get(ApiConstants.getDatamodelTableData, { sid }).subscribe({
      next: (datamodel: any) => {
        if (datamodel && (datamodel.t_list_query || datamodel.t_detail_query)) {
          this.currentDatamodel = datamodel;
          this.currentDatamodel.isQueryString = false; // Flag to identify type during save

          const queryContent = datamodel.t_list_query || datamodel.t_detail_query;
          this.queryEditor.value = this.queryEditor.replacePlaceholders(queryContent, this.applicationMetaData);
          this.queryEditor.currenttablename = datamodel.name;
          this.queryEditor.isQueryStringEditor = false; // Flag to show 'Query' in header
          this.queryEditor.toggleEditor();
        }
      },
      error: (error) => {
        // Handle error if needed
      }
    });
  }

  saveAndRefresh(isSave: boolean) {
    if (isSave) {
      this.loading = true;
      const modelId = this.config.data?.referredId || this.config.data?.node?.sid;
      this.getReferences(modelId, 'getReferences')
        .then((value: any) => {
          this.config.data.referenceList = value;
          this.loadNodes(true);
        })
        .catch((error) => {
          // Handle error if needed
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  ngOnDestroy(): void {
    console.log('on destroy triggered for page-variables');
  }
}



